import React, { useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { formatBytes } from "../data/storageUtils";

const ModuleUsageCard = ({ modules = [], adminId, loadModuleFiles, doughnutOptions }) => {
  const [moduleSort, setModuleSort] = useState("size");
  const [showAllModules, setShowAllModules] = useState(false);

  const moduleChart = useMemo(() => {
    const labels = modules.map((m) => m.module);
    const data = modules.map((m) => m.total_bytes || 0);
    return {
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: ["#0f4c5c", "#1982c4", "#6a4c93", "#f72585", "#ff9f1c", "#2ec4b6", "#8ac926"],
            borderWidth: 0,
          },
        ],
      },
    };
  }, [modules]);

  const moduleRows = useMemo(() => {
    const sorted = [...modules].sort((a, b) => {
      if (moduleSort === "name") return (a.module || "").localeCompare(b.module || "");
      if (moduleSort === "files") return (b.file_count || 0) - (a.file_count || 0);
      return (b.total_bytes || 0) - (a.total_bytes || 0);
    });
    const total = sorted.reduce((sum, row) => sum + (row.total_bytes || 0), 0);
    const visible = showAllModules ? sorted : sorted.slice(0, 6);
    return visible.map((row) => ({
      ...row,
      percent: total ? Math.round(((row.total_bytes || 0) / total) * 100) : 0,
    }));
  }, [modules, moduleSort, showAllModules]);

  const handleRowClick = (moduleName) => {
    if (!adminId || !moduleName) return;
    loadModuleFiles(adminId, moduleName);
  };

  return (
    <div className="card border-0 shadow-sm rounded-4 h-100 mb-4 sa-card sa-module-card">
      <div className="card-header bg-white border-0 py-3 rounded-top-4">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h5 className="mb-0 fw-bold">Module Usage</h5>
            <p className="text-muted small mb-0">Storage consumption by module for this admin.</p>
          </div>
          <div className="d-flex align-items-center sa-module-controls">
            <select
              className="form-select form-select-sm"
              value={moduleSort}
              onChange={(e) => setModuleSort(e.target.value)}
            >
              <option value="size">Sort: Size</option>
              <option value="files">Sort: Files</option>
              <option value="name">Sort: Name</option>
            </select>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowAllModules((prev) => !prev)}
            >
              {showAllModules ? "Show top 6" : "Show all"}
            </button>
          </div>
        </div>
      </div>
      <div className="card-body">
        {modules.length === 0 && <div className="text-muted">No module data available yet.</div>}
        {modules.length > 0 && (
          <div className="row g-3 align-items-start">
            <div className="col-md-4">
              <div className="sa-chart-compact">
                <Doughnut data={moduleChart.data} options={doughnutOptions} />
              </div>
            </div>
            <div className="col-md-8">
              <div className="table-responsive">
                <table className="table table-sm mb-0 sa-table-compact sa-table-tight">
                  <thead className="table-light">
                    <tr>
                      <th>Sr</th>
                      <th>Module</th>
                      <th>Used</th>
                      <th>Files</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {moduleRows.map((module, idx) => (
                      <tr key={module.module} className="sa-click-row" onClick={() => handleRowClick(module.module)}>
                        <td className="text-muted">{idx + 1}</td>
                        <td>
                          <div className="fw-semibold">{module.module}</div>
                          <div className="sa-mini-bar">
                            <span style={{ width: `${module.percent}%` }}></span>
                          </div>
                        </td>
                        <td>{formatBytes(module.total_bytes)}</td>
                        <td>{module.file_count}</td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-primary sa-btn-xs"
                            onClick={() => handleRowClick(module.module)}
                          >
                            View Files
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {modules.length > 6 && !showAllModules && (
                  <div className="text-muted small mt-2">
                    Showing top 6 modules. Use Show all for the full list.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModuleUsageCard;
